import $ from 'jquery';
import _ from 'underscore';
import Raven from 'util/raven';
import { track } from 'util/analytics';
import flags from 'util/flags';
import {
    $body,
    $window,
    jqGetElemById,
    isMobile,
    jqOffOn,
    setScrollPosition,
} from 'util/global';
import { crop } from 'util/ui';
import {
    TRANSITION_SPEED_FAST,
    CLASS_NAME_ACTIVE,
    CLASS_NAME_DISABLED,
    EVT_SUBSCRIPTION_SETTINGS_TRIGGER_CLICKED,
} from 'util/constants';
import { showStandardErrorModal, errorModalHandler } from 'app/error_modal';
import { initGenericControllers } from 'app/generic_controllers';


let enableNewStripeCheckout = false;


/** @namespace */
var Paid = {};


/**
 * Current gift message id for gift subscriptions
 * @type {Number}
 */
var currentGiftMessageId = null;


var mxpTrackTimeout = function (stepName) {
    track('payment timeout error', {
        step: stepName
    });
};


/**
* @singleton
* Controller for the sell page modal.
*/
Paid.SellPage = new function () {
    const self = this;

    /**
    * @controller
    * @privileged
    */
    self.controller = ($element) => {
        // learn about subscriptions link
        const $subscriptionInfoModal = jqGetElemById('subscription-info-modal').detach();
        if ($subscriptionInfoModal.length) {
            $element.find('.learn-more').on('click', (e) => {
                e.preventDefault();
                $subscriptionInfoModal.clone().modal();
            });
        }
    };

    return self;
};

/**
 * Toggle bulk pricing text
 * @param {JQuery} $container
 */
Paid.bulkPricing = ($container) => {
    $container.find('.bulk-pricing-trigger').on('click', () => {
        $container.toggleClass('expanded');
    });
};


Paid.giftSubscribeOptIn = function ($checkbox) {
    $checkbox.on('change', (e) => {
        const checkbox = e.target;

        if (checkbox.disabled) {
            return;
        }

        const $checkbox = $(checkbox);
        const url = $checkbox.data('url');

        checkbox.disabled = true;

        $.post(url)
            .done((response) => {
                if (response.success === true) {
                    $checkbox.data('url', response.new_url);
                } else {
                    showStandardErrorModal();
                }
            })
            .fail(() => {
                $checkbox.prop('checked', !$checkbox.prop('checked'));
                showStandardErrorModal();
            })
            .always(() => {
                checkbox.disabled = false;
            });
    });
};

/**
 * Toggle between courses and practice in courses paywall section
 * @param {JQuery} $container
 */
Paid.paywallCourses = function ($container) {
    var $toggleBtns = $container.find('.btn-group .btn');
    var activeClass = 'active';

    $toggleBtns.on('click', function () {
        var $btn = $(this);
        if (!$btn.hasClass(activeClass)) {
            $toggleBtns.toggleClass(activeClass);
            $container.toggleClass('practice');
            track('ui', {
                element_id: 'clicked_paywall_courses_tab',
                tab_name: $btn.data('ax-tab'),
                action: 'click',
            });
        }
    });
};

/**
 * Scroll to the premium feature list by default
 * @param {JQuery} $container
 */
Paid.setFeaturesScroll = ($container) => {
    $container.scrollLeft($container[0].scrollWidth);
};


// checkout
// --------------------------------------------------

/** @namespace */
Paid.Checkout = {};

let currentTransactionData;
let $prestatusModal = null;


/**
* Handle successful credit card entry
* @param {object} token - object with token and credit card info
* @param {object} args - additional shipping or billing info if submitted (likely never used for
*                        our purposes)
* @see https://stripe.com/docs/checkout#integration-custom
* @see https://stripe.com/docs/api#tokens
*/
var stripeTokenHandler = function (token, args, returnPromise) {
    const subscriptionData = jqGetElemById('paid-subscribe').data();
    const { url } = subscriptionData;
    const data = {
        plan: currentTransactionData.plan,
        stripeToken: token.id,
    };
    const opts = {
        url,
        data,
        type: 'POST',
        timeout: 60000, // 60s
    };

    if (currentGiftMessageId) {
        data.gift_message_id = currentGiftMessageId;
    }

    if (isStripeOneTimePayment() && !currentGiftMessageId) {
        opts.url = jqGetElemById('stripe-one-time-payment').data('url');
        opts.data.payment_plan_id =
            currentTransactionData.paymentPlanId || currentTransactionData.plan;
        opts.data.amount = currentTransactionData.amount;
    }

    var promise = $.ajax(opts);

    if (returnPromise === true) {
        return promise;
    }

    promise
        .then((response) => {
            if (subscriptionData.callback) {
                Paid[subscriptionData.callback](response);
            } else {
                const $div = $(document.createElement('div')).append(response);
                $body.append($div);
                initGenericControllers($div);
            }
        })
        .fail((response) => {
            if (response.statusText === 'timeout') {
                Raven.captureMessage('stripeTokenHandler: timeout error');
                mxpTrackTimeout('Sending token to backend');
            }
            errorModalHandler(response);
        });


    if ($prestatusModal !== null) {
        // INR one time payments display the "Select your payment option" modal,
        // and we want to close that here so the user can go back to where they
        // were after they pay.
        $prestatusModal.modal('show');
    }
};

/**
* Check whether the payment is a regular Stripe subscription or a one time
* payment for Indian users. The plan data for regular Stripe subscriptions is
* defined in payment/includes/checkout_button.html. The plan data for one time
* payments is defined in payment/includes/stripe_onetime_payment_button.html and
* has a "data-payment-plan-id" attribute. This function checks for the
* attribute.
*/
var isStripeOneTimePayment = function () {
    return typeof currentTransactionData.paymentPlanId !== "undefined"
    || currentTransactionData.planType === 'lifetime'
    || currentTransactionData.planType === 'stripe_onetime_year';
}

/**
* Make adjustments to raw data from a checkout trigger button
* @param {object} data - result of $button.data()
*/
const processRawData = (data) => {
    const processedData = _.extend({}, data);

    const excludedProperties = [
        'controller',
        'controllerInited',
        'loadedAxId',
        'axId',
        'axType',
        'majorAmount',
        'ax_active',
    ];

    excludedProperties.forEach((prop) => {
        delete processedData[prop];
    });

    processedData.name = processedData.name
        .replace('^2', ' Premium')
        .replace('[USD]', '') // let's assume [USD] unless it's different
        .trim();

    return processedData;
};

/**
 * Flag for if preload has finished, true even if failed
 * @type {Boolean}
 */
var preloadDone = false;

/**
 * Flag for preload failed
 * @type {Boolean}
 */
var preloadFailed = false;

/**
 * The Stripe object returned by StripeCheckout.configure({ ... });
 * @type {Object}
 */
var checkoutObject = null;

/**
* Checkout button controller (Stripe)
*/
Paid.Checkout.trigger = (function () {
    var enableTrigger = function ($trigger) {
        var triggerData = $trigger.data();

        $trigger
            .removeClass('disabled')
            .removeAttr('disabled')
            .data('ax-id', triggerData.loadedAxId)
            .attr('data-ax-id', triggerData.loadedAxId);    // updating attr so that html gets updated too

        jqOffOn($trigger, 'click.checkout', (e) => {
            e.preventDefault();
            openCheckout(checkoutObject, triggerData);
        });
    };

    var handleError = function ($trigger) {
        // $trigger.hide();
    };

    var $loadingModal;

    return function ($trigger) {
        // check flags first
        if (preloadDone) {
            if (preloadFailed) {
                handleError($trigger);
            } else {
                enableTrigger($trigger);
            }
            // if no flags, wait for events
        } else {
            $body.on('checkout.preloadDone', () => {
                enableTrigger($trigger);
            });
            $body.on('checkout.preloadFailed', () => {
                handleError($trigger);
            });
        }

        $trigger.filter('.disabled').on('click.checkout', (e) => {
            e.preventDefault();
            if (!$loadingModal) {
                $loadingModal = jqGetElemById('checkout-loading-modal');
            }
            $loadingModal.modal();

            // set higher z-index than paywall modal
            $loadingModal.parent('.modal-bg').css('z-index', 1042);
        });
    };
})();


/**
 * Test for window.StripeCheckout and start preloading Stripe assets once it exists
 */
var preloadCheckoutObject = (function () {
    // used to only display error once
    var errorTriggered = false,
        preloadStarted = false;

    return function () {
        if (preloadStarted) {
            return;
        }
        preloadStarted = true;

        const data = jqGetElemById('stripe-checkout-data').data();

        if (!data) {
            return;
        } else if (!data.giftSubscription && data.useCustomForm) {
            return;
        }

        enableNewStripeCheckout = data.enableNewStripeCheckout === true;

        var pollCount = 0,
            pollInterval = 200, // 200ms
            pollLimit = 75;     // (15s * 1000ms) / pollInterval

        // start a poll to check that StripeCheckout has loaded
        var poll = setInterval(function () {
            pollCount++;

            // if StripeCheckout has loaded, clear the interval and enable the buttons
            if ('StripeCheckout' in window) {
                clearInterval(poll);

                // Create a new checkout object.
                // This only needs to happen once. Each button shares the configuration data,
                // plus this function preloads all of the modal assets.
                if (checkoutObject === null) {
                    var checkoutData = {
                        key: data.key,
                        image: data.image,
                        email: data.email || null,
                        token: stripeTokenHandler,
                        opened: function () {
                            track('ui', {
                                element_id: 'stripe_checkout_modal_opened'
                            });
                        },
                        closed: function () {
                            track('ui', {
                                element_id: 'stripe_checkout_modal_closed'
                            });
                        }
                    };

                    if (data.panelLabel) {
                        checkoutData.panelLabel = data.panelLabel;
                    }

                    checkoutObject = StripeCheckout.configure(checkoutData);
                }

                // set the flag and trigger the done event
                preloadDone = true;
                $body.trigger($.Event('checkout.preloadDone'));

                // if the poll has gone on for too long, clear the interval and log it to Sentry
            } else if (pollCount === pollLimit) {
                clearInterval(poll);

                // set the flag and trigger the error event
                preloadDone = true;
                preloadFailed = true;
                $body.trigger($.Event('checkout.preloadFailed'));

                if (!errorTriggered) {
                    errorTriggered = true;

                    if (jqGetElemById('sellpage-wrapper').length) {
                        Raven.captureMessage('Paid.Checkout.trigger: ' +
                            'poll timeout waiting for StripeCheckout.');
                        mxpTrackTimeout('Loading checkout.js');
                    }
                }
            }
        }, pollInterval);
    };
})();

/**
* Opens the checkout modal
* @param {object} checkoutObj
* @param {object} data
*/
const openCheckout = (checkoutObj, data) => {
    // process the data
    const processedData = processRawData(data);

    // save the data for later
    currentTransactionData = _.extend({}, processedData);

    const newStripeEnabled =
        (processedData.planType === 'annual' || processedData.planType === 'monthly')
        && ((processedData.currency === 'EUR' || processedData.currency === 'GBP')
            || enableNewStripeCheckout)
        && window.location.href.indexOf('/give-premium/') === -1;

    if (newStripeEnabled) {
        const params = { plan_id: processedData.plan };
        $.get('/premium/stripe/create-checkout-session/', params)
            .fail(showStandardErrorModal)
            .then((response) => {
                const sessionInfo = response.results.checkout_session;
                if (!sessionInfo) {
                    showStandardErrorModal();
                }

                const stripe = Stripe(sessionInfo.public_key);
                stripe
                    .redirectToCheckout({ sessionId: sessionInfo.session_id })
                    .then(() => showStandardErrorModal())
            })
    } else {
        // open the checkout modal
        delete processedData.plan;
        delete processedData.planType;
        checkoutObj.open(processedData);
    }
};

/**
 * Start preloading checkout object for situations in which there are no triggers (full-page
 * mobile, for example)
 */
Paid.Checkout.preload = function () {
    preloadCheckoutObject();
};

/**
* Handle the checkout response
* @param {JQuery} $element - the modal container
*/
Paid.Checkout.response = function ($element) {
    const data = $element.data();
    const intervalLimit = 60; // quit after 60s
    let nextUrl = window.location.href;
    let intervalCount = 0;
    let interval;

    const $successBtn = jqGetElemById('paid-status-success-btn');

    const handleSuccess = () => {
        if (data.showAppleWarning) {
            jqGetElemById('existing-apple-subscription-warning').modal();
        }

        $element.addClass('loaded success');
        nextUrl = $successBtn.prop('href');
    };

    if (data.success && data.displaySuccess) {
        handleSuccess();
    }
    // successfully created a subscription object
    else if (data.success) {

        // start polling
        interval = setInterval(function () {
            // check the status
            $.get(data.url, { s: data.subscription }).then(function (result) {
                if (result === 'active') {
                    handleSuccess();
                    clearInterval(interval);
                }
            }).fail((response) => {
                clearInterval(interval);
                errorModalHandler(response);
            });

            // bump the count
            intervalCount++;
            if (intervalCount >= intervalLimit) {
                $element.addClass('loaded error');
                clearInterval(interval);
                Raven.captureMessage('Paid.Checkout.response: timeout error');
                mxpTrackTimeout('Checkout subscription status');
            }
        }, 1000);


        // error creating subscription
    } else {
        $element.addClass('loaded error');
    }

    // hide active modal
    if ($prestatusModal !== null) {
        $prestatusModal.modal('hide');
    }

    // show the modal
    $element.modal();
    $element.on('hide', () => {
        $body.addClass('disabled');
        window.location.href = nextUrl;
    });
};

/**
* "Prestatus" modal to fill the gap between getting a token and checking the payment status
*/
Paid.Checkout.prestatus = function ($modal) {
    $modal.modal({
        show: false
    });

    $modal.on('show', function () {
        // still visible with Paytm-style modal
        $('.sell-page.visible').removeClass('visible');
    });

    $prestatusModal = $modal;
};


// Cancel subscription survey
// --------------------------------------------------

/**
 * Instead of canceling the plan right away, show a survey modal first and cancel once the survey
 * is successfully submitted. Events fire on the cancellation link.
 * Available events: survey.complete, survey.beforeshown
 * @param  {Event}
 * @example
 *     $cancelLink.on('click', handleShowSurvey);
 *     $window.on('survey.complete', actuallyCancelTheSubscription);
 */
var handleShowSurvey = (function () {
    var $modal = null;
    var submitted = false;
    var errorListTmpl = _.template('<ul class="errorlist"><%= list %></ul>');
    var errorItemTmpl = _.template('<li><%- message %></li>');
    var surveyEvent = function ($source, name) {
        return $window.trigger($.Event(`survey.${name}`));
    };

    return function (e) {
        e.preventDefault();

        const $source = $(this);

        if (submitted) {
            surveyEvent($source, 'complete');
            return;
        }

        if ($modal === null) {
            $modal = jqGetElemById('paid-cancel-survey');

            if (!$modal.length) {
                showStandardErrorModal();
                return;
            }

            $modal.on('submit', function (e) {
                e.preventDefault();

                $.post($modal.attr('action'), $modal.serialize())
                    .fail(errorModalHandler)
                    .then(function (response) {
                        if (response.success) {
                            submitted = true;
                            $modal.modal('hide');
                            surveyEvent($source, 'complete');
                        } else {
                            setScrollPosition(0, {
                                $container: $modal.parent(),
                            });
                            const errorMsgs = [];
                            for (var error in response.errors) {
                                errorMsgs.push(response.errors[error][0]);
                            }

                            var errorList = errorMsgs.map(function (message) {
                                return errorItemTmpl({
                                    message: message
                                });
                            }).join('');

                            _.defer(function () {
                                $modal.find('.errorlist').remove();
                                $modal.find('.modal-body').prepend(errorListTmpl({
                                    list: errorList
                                }));
                            });
                        }
                    })
            });
        }

        surveyEvent($source, 'beforeshown');
        $modal.modal();
    };
})();


// Payment methods form
// --------------------------------------------------

/** @namespace */
Paid.Payment = {};

var $cancelPaymentModal = null,
    activeCancelData = null;

var cancelPayment = function (data) {
    $.get(data.url, data)
        .then(() => {
            // response is either 'canceled' or 'error'
            // reload for both cases
            $body.addClass('disabled');
            window.location.reload();
        })
        .fail(errorModalHandler);
};

/**
* Button for cancel confirmation modal
*/
Paid.Payment.cancellationModalTrigger = function ($link) {
    $link.on('click', function (e) {
        e.preventDefault();
        if ($link.hasClass('disabled')) {
            return;
        }
        activeCancelData = $link.data();
        if (activeCancelData.force) {
            cancelPayment(activeCancelData);
        } else {
            if ($cancelPaymentModal !== null) {
                $cancelPaymentModal.modal('show');
            }
        }
    });
};

/**
* Modal for canceling subscription
*/
Paid.Payment.cancelModal = function ($modal) {
    $cancelPaymentModal = $modal.clone(true);

    $cancelPaymentModal.modal({
        show: false
    });

    $cancelPaymentModal.find('#paid-cancel').on('click', (e) => {
        const $upgradeModal = jqGetElemById('payment-switch-plan-modal').clone(true);
        const cancelButton = e.currentTarget;

        if ($upgradeModal.length) {
            $cancelPaymentModal.modal('hide');
            $upgradeModal.find('.cancel-link').on('click', () => {
                handleShowSurvey.call(cancelButton, e);
            });
            $upgradeModal.addClass('canceling');
            $upgradeModal.modal();
        } else {
            $cancelPaymentModal.modal('hide');
            handleShowSurvey.call(cancelButton, e);
        }
    });

    $window.on('survey.complete', () => {
        jqGetElemById('acct-settings-subscription').find('.cancel a').addClass('disabled');
        if (activeCancelData.isPaytm) {
            const $cancelConfirmModal = jqGetElemById(`cancel-confirm-${activeCancelData.id}`);
            $cancelConfirmModal.modal('show');
            $cancelConfirmModal.find('#confirm-btn').on('click', () => {
                cancelPayment(activeCancelData);
            });
        } else if (activeCancelData !== null) {
            cancelPayment(activeCancelData);
        }
    });
};


/**
 * Renew button
 * @param {JQuery} $button
 */
Paid.Payment.Renew = ($button) => {
    $button.on('click', (e) => {
        e.preventDefault();
        $button.addClass(CLASS_NAME_DISABLED);
        $body.addClass(CLASS_NAME_DISABLED);
        $.get($(e.currentTarget).attr('href'))
            .then(() => window.location.reload())
            .fail(errorModalHandler)
            .fail(() => $body.removeClass(CLASS_NAME_DISABLED));
    });
};


/**
 * Opens Stripe checkout form for card updating
 * @param {JQuery} $button
 */
Paid.Payment.UpdateCard = ($button) => {
    $button.on('click', (e) => {
        if (!$button.hasClass(CLASS_NAME_DISABLED)) {
            const $cardInfo = jqGetElemById('card-info');
            const $spinner = $cardInfo.find('.spinner');

            const { key, image, email, name, url, buttonLabel } = $(e.target).data();

            const stripeData = {
                key,
                image,
                name,
                url,
                email: email || null,
                allowRememberMe: false,
            };

            const stripeConfig = Object.assign({
                panelLabel: buttonLabel,
                token(token) {
                    const visibleClass = 'visible';
                    $button.addClass(CLASS_NAME_DISABLED);
                    $spinner.addClass(visibleClass);
                    $.post(url, { stripeToken: token.id })
                        .then(() => {
                            ['last4', 'exp_month', 'exp_year'].forEach((property) => {
                                $cardInfo.find(`.${property}`).text(token.card[property]);
                            });
                            $button.removeClass(CLASS_NAME_DISABLED);
                            $spinner.removeClass(visibleClass);
                        })
                        .fail(errorModalHandler);
                },
                closed() {
                    $button.removeClass(CLASS_NAME_DISABLED);
                },
            }, stripeData);

            $button.addClass(CLASS_NAME_DISABLED);
            StripeCheckout
                .configure(stripeConfig)
                .open(stripeData);
        }
    });
};


/**
 * Opens subscription settings modal.
 * @param {JQuery} $trigger
 */
Paid.Payment.ShowSubscriptionSettings = ($trigger) => {
    $trigger.on('click', (e) => {
        e.preventDefault();
        window.dispatchEvent(new Event(EVT_SUBSCRIPTION_SETTINGS_TRIGGER_CLICKED));
        track('ui', {
            element_id: 'clicked_subscription_settings',
            action: 'click',
        });
    });
};


/**
 * Controller for the subscription settings modal component wrapper.
 * @param {JQuery} $modal
 */
Paid.Payment.SubscriptionSettingsModal = ($modal) => {
    $window.on('SubscriptionSettingsModal.error', (e, data) => {
        showStandardErrorModal();
    });
};


/**
 * Controller.
 * Shows confirmation modal when switching to yearly plan.
 * @param {JQuery} $trigger
 */
Paid.switchPlanModal = ($trigger) => {
    $trigger.on('click', () => {
        jqGetElemById('payment-switch-plan-modal').clone(true).modal();
    });
};

/**
 * Handle redeeming a Premium perk (e.g. Nautilus subscription)
 * @param {JQuery} $form
 */
Paid.redeemPremiumPerk = ($form) => {
    $form.on('submit', (e) => {
        e.preventDefault();
        $form.find('button').prop('disabled', true);
        const formData = $form.data();
        const params = {
            perk_type: formData.perkType,
        };
        $.post($form.prop('action'), params)
            .fail(showStandardErrorModal)
            .then((response) => {
                if (response.success === true) {
                    if (formData.nextUrl) {
                        window.location.href = formData.nextUrl;
                    } else {
                        $form.parents('.modal').modal('hide');
                        jqGetElemById('perk-modal-trigger').addClass(CLASS_NAME_ACTIVE);
                    }
                } else {
                    showStandardErrorModal();
                }
            });
    });
};

/**
 * Controller for coupon time remaining countdown
 */
Paid.couponCountdown = (function () {
    var $containers = null,
        timerInterval = null,
        previousText = null;

    // prepend 0 for single-digit numbers
    var zeroPad = function (n) {
        n = n.toString();
        return n < 10 ? '0' + n : n;
    };

    // get current elapsed time and re-render timer
    var update = function (startTime, secondsRemaining, template, isInExperiment, forceUpdate) {
        var elapsedTime = Math.round(new Date().getTime() / 1000) - startTime,
            currentRemaining = secondsRemaining - elapsedTime;

        // only update if something has changed
        if (elapsedTime > 0 || forceUpdate) {
            if (currentRemaining < 0) {
                currentRemaining = 0;
            }

            let days;
            let hours;
            let minutes;
            let seconds;
            let data;

            // This logic in duplicated in
            // brilliant.payment_processor.domain.DomainPaymentCoupon.time_remaining_info
            // If you change anything here, make sure that function is updated to match
            if (isInExperiment) {
                hours = ~~(currentRemaining / 3600); // === Math.floor
                minutes = ~~((currentRemaining % 3600) / 60); // === Math.floor
                seconds = currentRemaining % 60;
                data = {
                    days: 0, // logged_in_premium_banners_04_2021: remove if experiment wins
                    hours,
                    minutes,
                    seconds,
                };
            } else {
                days = ~~(currentRemaining / 86400); // === Math.floor
                hours = ~~((currentRemaining % 86400) / 3600); // === Math.floor
                minutes = ~~((currentRemaining % 3600) / 60); // === Math.floor
                seconds = currentRemaining % 60;
                data = {
                    days: days,
                    hours: days > 0 ? zeroPad(hours) : hours,
                    minutes: zeroPad(minutes),
                    seconds: zeroPad(seconds),
                };
            }

            const newText = template(data);

            if (newText !== previousText) {
                $containers.text(newText);
                previousText = newText;
            }
        }

        return currentRemaining;
    };

    return function ($container) {
        const template = _.template($container.data('template'));
        const secondsRemaining = $container.data('seconds');
        const isInExperiment = $container.data('is-in-experiment');
        const startTime = Math.round(new Date().getTime() / 1000);

        // add this container to master containers object
        $containers = $containers === null ? $container : $containers.add($container);

        // update the text and show the container
        _.defer(function () {
            update(startTime, secondsRemaining, template, isInExperiment, true);
            $containers.removeClass('hide');
        });

        // update both countdowns with one interval
        if (timerInterval === null) {
            timerInterval = setInterval(function () {
                if (update(startTime, secondsRemaining, template, isInExperiment) <= 0) {
                    clearInterval(timerInterval);
                    $containers.parents('.hdr-coupon, .premium-banner').remove();
                }
            }, 250);
        }
    };
})();

/**
 * Show the stripe/paypal payment choice modal
 * @param {('monthly'|'annual'|'lifetime')} plan
 * @param {boolean} [hasSinglePaymentOption] - true if only stripe or only paypal, false if both
 */
function showPaymentChoiceModal(plan, hasSinglePaymentOption = false) {
    let $modal = null;
    let $sellPage = null;

    if ($modal === null) {
        $modal = jqGetElemById('paypal-modal');
        $sellPage = $modal.find('.sell-page');

        $modal.removeClass('hide').appendTo($body);
        $modal.find('.close').on('click', () => {
            $sellPage.removeClass('visible');
            crop(false);
            let mobileButton = $('#payment-request-button-mobile-button');
            if (mobileButton.length > 0) {
                initPaymentRequest(mobileButton);
            }
        });
        let paymentRequestButtonElement = $modal.find(`.${ plan } .payment-request-button`);
        if (paymentRequestButtonElement.length > 0) {
            initPaymentRequest(paymentRequestButtonElement);
        }
    }

    _.defer(() => {
        // close existing modal
        const $existingModal = $('.modal-bg .modal:visible');
        if ($existingModal.length) {
            $existingModal.modal('hide');
        }

        $modal.removeClass('annual monthly lifetime').addClass(plan);
        $modal.toggleClass('single-option', hasSinglePaymentOption);
        $sellPage.addClass('visible');
        crop();

        if (currentGiftMessageId) {
            $modal.find('[name=gift_message_id]').val(currentGiftMessageId);
        }
    });
}


/**
 * Button to show the stripe/paypal payment choice modal
 * @param {JQuery} $trigger
 */
Paid.paypalModalTrigger = function ($trigger) {
    $trigger.on('click', function () {
        const { plan, singlePaymentOption } = $trigger.data();
        showPaymentChoiceModal(plan, singlePaymentOption);
    });
};


Paid.paypalCheckoutButtons = function ($container) {
    $container.find('.paypal a,.paypal button').on('click', function (e) {
        $(this).parents('.paypal').addClass('loading');
    });
};

/**
 * Controller for sales card B2 buttons
 * @param {JQuery} $btn
 */
Paid.salesCardTrigger = function ($btn) {
    import('sales_card.less');
    if (isMobile) {
        import('mobile/sales_card.less');
    }
};

/**
 * Switch between positions in paywall modal
 * @param {JQuery} $container
 */
Paid.switchPaywallPosition = function ($container) {
    var $positionMarker = $container.find('#prac-markers .prac-marker');
    var $prevPositionBtn = $container.find('#prev-marker-btn');
    var $nextPositionBtn = $container.find('#next-marker-btn');
    var $feature = $container.find('.spm-feature');
    var lastPosition = $positionMarker.length;
    var firstPosition = 1;
    var position = firstPosition;
    var activeClass = 'current'
    var disabledClass = 'disabled';

    var switchPostion = function ($marker) {
        $feature.removeClass(activeClass);
        $feature.filter('[data-position="' + position + '"]').addClass(activeClass);

        $positionMarker.removeClass(activeClass);
        if ($marker) {
            $marker.addClass(activeClass);
        } else {
            $positionMarker.filter('[data-position="' + position + '"]').addClass(activeClass);
        }

        $prevPositionBtn.removeClass(disabledClass);
        $nextPositionBtn.removeClass(disabledClass);
        if (position == firstPosition) {
            // current slide is first slide
            // disable prev button
            $prevPositionBtn.addClass(disabledClass);
        } else if (position == lastPosition) {
            // current slide is last slide
            // disable next button
            $nextPositionBtn.addClass(disabledClass);
        }
    };

    $positionMarker.on('click', function (e) {
        e.preventDefault();

        var $this = $(this);
        var newPosition = $this.data('position');
        if (newPosition != position) {
            position = newPosition;
            switchPostion($this);
        }
    });

    $prevPositionBtn.on('click', function (e) {
        e.preventDefault();

        if (!$(this).hasClass(disabledClass)) {
            position--;
            switchPostion();
        }
    });

    $nextPositionBtn.on('click', function (e) {
        e.preventDefault();

        if (!$(this).hasClass(disabledClass)) {
            position++;
            switchPostion();
        }
    });
};


/** Scroll to top after submitting post-checkout survey */
Paid.premiumSurvey = () => {
    setScrollPosition(0, {
        animate: true,
        duration: TRANSITION_SPEED_FAST,
    });
};


/**
 * Adds twinkling stars to the container.
 * @param {JQuery} $container
 */
Paid.twinklingStars = function ($container) {
    // remove old stars in case of re-applying this method to some container
    $container.find('.premium-stars').remove();

    var data = $container.data();
    if (!data) {
        return;
    }

    var containerOffset = $container.offset();
    var containerWidth = $container.outerWidth();
    var containerHeight = $container.outerHeight();
    var stars = [];
    var STAR_COUNT = data['starCount'];
    var PADDING = 10; // padding between excluded elements and stars, px
    var DURATION = 4000; // duration of one animation cycle, ms
    var COMMON_DELAY = 5000; // stars start twinkling after this time, ms

    var importantElements = [];
    $container.find('.no-stars').each(function () {
        var $elem = $(this);
        var offset = $elem.offset();
        // calculate coordinates of the elements inside the container
        importantElements.push({
            left: offset.left - containerOffset.left - PADDING,
            right: offset.left + $elem.outerWidth() - containerOffset.left + PADDING,
            top: offset.top - containerOffset.top - PADDING,
            bottom: offset.top + $elem.outerHeight() - containerOffset.top + PADDING
        });
    });

    /**
     * Determines whether a star in this position would interfere with important elements.
     * @param {{left: number, top: number}} position
     * @returns {boolean}
     */
    var isBadPosition = function (position) {
        // calculate coordinates inside the container, assuming that star size is 3x3 px
        var starX = (position.left / 100) * containerWidth + 1.5;
        var starY = (position.top / 100) * containerHeight + 1.5;

        for (var i = 0; i < importantElements.length; i++) {
            var elem = importantElements[i];
            if (starX < elem.left || starX > elem.right || starY < elem.top || starY > elem.bottom) {
                continue;
            }
            return true;
        }

        return false;
    };

    var starsContainer = document.createElement('span');
    starsContainer.className = 'premium-stars';

    for (var i = 0; i < STAR_COUNT; i++) {
        // try to generate a good position for the star, limit number of tries just in case
        var tries = 0;
        do {
            var position = {
                left: 0.5 + Math.random() * 99,
                top: 2 + Math.random() * 96
            };
            tries++;
        } while (isBadPosition(position) && tries < 50);

        stars[i] = document.createElement('span');
        stars[i].className = 'type-' + ~~(Math.random() * 3 + 1);
        stars[i].style.left = position.left + '%';
        stars[i].style.top = position.top + '%';

        starsContainer.appendChild(stars[i]);
    }

    $container.append(starsContainer);

    if (!data.static || data.static !== true) {
        _.defer(function () {
            for (var i = 0; i < STAR_COUNT; i++) {
                var delay = ~~(Math.random() * DURATION) + COMMON_DELAY;
                stars[i].style.webkitAnimationDuration = stars[i].style.animationDuration = DURATION + 'ms';
                stars[i].style.webkitAnimationDelay = stars[i].style.animationDelay = delay + 'ms';
            }
        });
    }
};

function getFreeTrialTrackingFunction(suffix) {
    suffix = suffix || null;
    return function (axId, extraParams) {
        extraParams = extraParams || {};
        if (suffix) {
            axId += '_' + suffix;
        }
        track('ui', _.extend({}, {
            element_type: 'button',
            element_id: axId,
            path: window.location.pathname,
            full_path: window.location.pathname + window.location.search
        }, extraParams));
    };
}

/**
 * Show free trial modal
 * @param {JQuery} $modal
 */
Paid.freeTrialModal = function ($modal) {
    const $options = $modal.find('.paywall-free-trial-options button');
    const $introContinueButton = $modal.find('#step-0-continue');
    const $freeTrialContinueButton = $modal.find('#free-trial-continue');
    const modalData = $modal.data();
    const activeClass = 'active';
    const bodyClass = 'paywall-free-trial-modal-active';
    let $modalBg;
    let selectedOption = null;

    const checkModalHeight = () => {
        if ($modalBg) {
            $modalBg.toggleClass('centered', $modal.outerHeight() < $window.height());
        }
    };

    var _track = $introContinueButton.length
        ? getFreeTrialTrackingFunction('chapter')
        : getFreeTrialTrackingFunction();

    $options.on('click', function () {
        if (selectedOption === null) {
            $freeTrialContinueButton.removeClass('disabled').removeAttr('disabled');
        }

        var $option = $(this);
        if (!$option.hasClass(activeClass)) {
            $options.removeClass(activeClass);
            $option.addClass(activeClass);
            selectedOption = $option.data('option');
        }
    });

    $introContinueButton.on('click', function () {
        if (modalData.introOnly) {
            if (modalData.nextChapterUrl) {
                _track('clicked_chapter_completion_next_chapter');
                window.location = modalData.nextChapterUrl;
            } else {
                _track('clicked_chapter_completion_premium_close');
                $modal.modal('hide');
            }
        } else {
            _track('clicked_free_trial_intro_continue');
            $modal.addClass('step-1');
        }
    });

    $freeTrialContinueButton.on('click', function () {
        if (!selectedOption) {
            return;
        }

        _track('clicked_free_trial_continue', {
            free_trial_selected_option: selectedOption
        });

        if (selectedOption === 'limited') {
            if ($modal.find('.step-2').length) {
                $modal
                    .removeClass('step-1')
                    .addClass('step-2');
                $modal.find('#free-trial-close').on('click', function () {
                    _track('clicked_free_trial_limited_close');
                    $modal.modal('hide');
                });
            } else {
                $modal.modal('hide');
            }
        } else {
            $modal.modal('hide');
            _.defer(function () {
                _track(modalData.premiumAxId);
            });
            window.open(modalData.premiumUrl);
        }
    });

    $modal.on('show', () => {
        $body.addClass(bodyClass);
        _track('viewed_free_trial_modal');
        _.defer(() => {
            $modalBg = $modal.parent();
            checkModalHeight();
        });
        $window.on('resize.freeTrialModal', _.throttle(checkModalHeight, 16));
    });

    $modal.on('hide', () => {
        $body.removeClass(bodyClass);
        $window.off('resize.freeTrialModal');
    });

    $modal.modal({
        backdrop: 'static',
    });

};


/**
 * Recurring gift card.
 * @param {JQuery} $container
 */
Paid.giftPurchaseList = function ($container) {
    let templates = {
        giftSummaryModal: _.template(jqGetElemById('gift-summary-modal-template').text()),
        turnOffRecurringGiftModal: _.template(jqGetElemById('turn-off-recurring-gift-modal-template').text()),
    };

    $container.find('.recurring-gift').each((i, elem) => {
        const $gift = $(elem);
        const data = $gift.data();

        const updateGiftParams = (state) => {
            const dataKey = 'subscription-state';
            $gift.attr(`data-${dataKey}`, state);
            $gift.data(dataKey, state);
        };

        $gift.find('.show-gift-summary-modal').on('click', () => {
            const $giftSummaryModal = $(templates.giftSummaryModal(data));
            const $autoRenewNote = $giftSummaryModal.find('.auto-renew-note');

            $('.gift-summary-modal').remove(); // remove old modals if any
            $body.append($giftSummaryModal);
            $giftSummaryModal.modal();

            $giftSummaryModal.find('.turn-on-auto-renew').on('click', (e) => {
                e.preventDefault();

                const $link = $(e.currentTarget);
                if ($link.hasClass(CLASS_NAME_DISABLED)) {
                    return;
                }

                const turnOnAutoRenewURL = $link.attr('href');

                $link.addClass(CLASS_NAME_DISABLED);

                $.post(turnOnAutoRenewURL)
                    .fail(showStandardErrorModal)
                    .then((response) => {
                        if (response.state === 'active') {
                            $autoRenewNote.removeClass('onetime').addClass('recurring');
                            $link.removeClass(CLASS_NAME_DISABLED);
                            updateGiftParams(response.state);
                        } else {
                            showStandardErrorModal();
                        }
                    });
            });

            $giftSummaryModal.find('.turn-off-auto-renew').on('click', (e) => {
                e.preventDefault();

                const $link = $(e.currentTarget);
                if ($link.hasClass(CLASS_NAME_DISABLED)) {
                    return;
                }

                const turnOffAutoRenewURL = $link.attr('href');
                const $turnOffRecurringGiftModal = $(templates.turnOffRecurringGiftModal(data));

                $giftSummaryModal.modal('hide');

                $('.turn-off-recurring-gift-modal').remove(); // remove old modals if any
                $body.append($giftSummaryModal);
                $turnOffRecurringGiftModal.modal();

                $turnOffRecurringGiftModal.on('hide', () => {
                    $giftSummaryModal.modal('show');
                    _.defer(crop);
                });

                $turnOffRecurringGiftModal.find('.turn-off').on('click', () => {
                    $turnOffRecurringGiftModal.find('button').addClass(CLASS_NAME_DISABLED);
                    $.post(turnOffAutoRenewURL)
                        .fail(showStandardErrorModal)
                        .then((response) => {
                            if (response.state === 'recurring_stopped'
                                    || response.state === 'non_renewing') {
                                $autoRenewNote.removeClass('recurring').addClass('onetime');
                                $turnOffRecurringGiftModal.modal('hide');
                                updateGiftParams(response.state);
                            } else {
                                showStandardErrorModal();
                            }
                        });
                });
            });
        });
    });
};

Paid.refundForm = ($form) => {
    const NOT_WHAT_I_EXPECTED_CLASS = 'not-what-i-expected';
    const ANOTHER_REASON_CLASS = 'another-reason';
    const { minChars, maxChars } = $form.data();

    // Reasons select menu
    const $reasons = $form.find('[name="refund_request_choice"]');

    // Get each TextAreas from sections 2 & 3
    const $textareas = $form.find('textarea');

    // Submit Button
    const $submit = $form.find('button');

    const isTextValid = (textareaName) => {
        const textarea = $textareas.filter(`[name="${textareaName}"]`)[0];
        const textLength = textarea.value.length;
        return textLength >= minChars && textLength <= maxChars;
    };

    const toggleSubmit = () => {
        let disableSubmitButton = false;

        // Refund choice must be selected
        if ($reasons.val().length === 0) {
            disableSubmitButton = true;
        }

        // validate all visible textareas
        $textareas.filter(':visible').each((index, textarea) => {
            if (!isTextValid(textarea.name)) {
                disableSubmitButton = true;
            }
        });

        $submit.prop('disabled', disableSubmitButton);
    };

    const toggleSecondStep = () => {
        const reason = $reasons.val();
        if (reason === '') {
            $form.removeClass(`${NOT_WHAT_I_EXPECTED_CLASS} ${ANOTHER_REASON_CLASS}`);
        } else {
            // show one textarea if user selects "This isn't what I was expecting",
            // show another textarea otherwise
            const notWhatIExpectedSelected = reason === '5';
            $form.toggleClass(NOT_WHAT_I_EXPECTED_CLASS, notWhatIExpectedSelected);
            $form.toggleClass(ANOTHER_REASON_CLASS, !notWhatIExpectedSelected);
        }
    };

    $textareas.on('change keyup', toggleSubmit);
    $reasons.on('change', () => {
        toggleSecondStep();
        toggleSubmit();
    });
};

//
// References:
//     (a) https://www.w3.org/TR/payment-request/
//     (b) https://stripe.com/docs/payment-request-api
//     (c) https://stripe.com/docs/stripe-js/elements/payment-request-button
//     (d) https://stripe.com/docs/stripe-js/reference#stripe-payment-request
//
// Requrement:
//     - https must be used
//
// For Apple Pay, the domain needs to verified by Apple
//     - https://stripe.com/docs/stripe-js/elements/payment-request-button#verifying-your-domain-with-apple-pay
//     - the .well-known/* file can be placed in media/well-known-dev/* for dev django
//     - the .well-known/* file for production will be handled in the infrastructure codebase
//     - the domain name in settings.BASE_URL will be used for verification
//
let currentSubscribeUrl = undefined;
let currentPlanId = undefined;
let paymentRequest = undefined;
let paymentRequestButton = undefined;

const initPaymentRequest = ($container) => {
    let buttonElement = $container.find('.payment-request-button-inner')[0];
    if (!paymentRequest) {
        let stripe = Stripe($container.data('stripe-public-key'));
        paymentRequest = stripe.paymentRequest({
            country: 'US',  //  country code of our Stripe account (see (d))
            currency: $container.data('currency').toLowerCase(),
            total: {
                label: $container.data('label'),
                amount: $container.data('amount'),
            },
            requestPayerName: true,
            requestPayerEmail: true,
        });
        const elements = stripe.elements();
        paymentRequestButton = elements.create('paymentRequestButton', {
            paymentRequest: paymentRequest,
            style: {
                paymentRequestButton: {
                    theme: 'light-outline', // default: 'dark'
                    height: '43px', // default: '40px', the width is always '100%'
                },
            },
        });

        // Check the availability of the Payment Request API first.
        paymentRequest.canMakePayment().then(function (result) {
            if (result) {
                paymentRequestButton.mount(buttonElement);
                currentSubscribeUrl = $container.data('subscribe-url');
                currentPlanId = $container.data('plan-id');
            } else {
                $('.paymentrequest').hide();
            }
        });

        // Get Stripe token on payment success.
        paymentRequest.on('token', function (ev) {
            $.post({
                url: currentSubscribeUrl,
                headers: { 'X-CSRFToken': $container.data('csrf-token') },
                data: {
                    plan: currentPlanId,
                    stripeToken: ev.token.id,
                    payment_request_method: ev.methodName,
                }
            }).done(function () {
                ev.complete('success');
                // TODO: go through appropriate success worfklow
                window.location.href = $container.data('success-url');
            }).fail(function () {
                ev.complete('fail');
                // TODO: show UI for failure
            });
        });
    } else if($container !== undefined && currentSubscribeUrl !== undefined) {
        paymentRequestButton.unmount();
        paymentRequest.update({
            currency: $container.data('currency').toLowerCase(),
            total: {
                label: $container.data('label'),
                amount: $container.data('amount'),
            }
        });
        currentSubscribeUrl = $container.data('subscribe-url');
        currentPlanId = $container.data('plan-id');
        paymentRequestButton.mount(buttonElement);
    }
};

Paid.PaymentRequest = ($container) => {
    if ($container.data('mobile-button') !== undefined) {
        initPaymentRequest($container);
    }
};

Paid.trackGiftPromoBanner = () => {
    setTimeout(
        () => track('ui', { action: 'viewed_gift_promo_header_banner' }),
        1,
    );
};

Paid.giftPromoBannerLoggedIn = ($banner) => {
    const $closeBtn = $banner.find('.close-btn');
    const data = $banner.data();

    $closeBtn.on('click', (e) => {
        e.preventDefault();
        $banner.remove();
        flags.update(data.flagName, true);
    });
};


module.exports = Paid;
